/**
 * The policy page's word on a re-assessment of this document: that one is
 * running, which change it is reading, and that its findings will update in
 * place; or that the last one stopped before it finished.
 *
 * Driven by the compliance cursor, not a refresh: the run starting and each of
 * its turns settling are pushed to the page, and the findings it revises arrive
 * on the same cursor.
 *
 * @module components/regulation/policy/PolicyReassessmentBanner
 */
import { Link } from "@tanstack/react-router";
import { AlertTriangleIcon, ArrowUpRightIcon, ClockIcon, RefreshCwIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "~/lib/utils";
import { timeAgo } from "../document/documentTime";
import type { PolicyReassessmentState } from "./PolicyReassessment.logic";

const COPY: Record<
  PolicyReassessmentState["kind"],
  (change: string) => { readonly heading: string; readonly body: string }
> = {
  queued: (change) => ({
    heading: `Re-assessment queued · ${change}`,
    body: "Inspector is about to re-read this document's open findings against the change. They update here as it goes.",
  }),
  running: (change) => ({
    heading: `Re-assessing findings · ${change}`,
    body: "Inspector is reading what changed and re-checking each open finding against it. Findings update here as it goes; nothing is decided for you.",
  }),
  failed: (change) => ({
    heading: `The re-assessment of ${change} did not finish`,
    body: "Findings marked “Document changed since” still describe the earlier text. Re-check one from its page, or open the run to see where it stopped.",
  }),
};

/** The elapsed time ticks here, not in the page, so a running clock re-renders one line. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

export function PolicyReassessmentBanner({
  state,
  orgSegment,
}: {
  readonly state: PolicyReassessmentState;
  readonly orgSegment: string;
}) {
  const now = useNow(10_000);
  const { heading, body } = COPY[state.kind](state.change);
  const Icon =
    state.kind === "failed" ? AlertTriangleIcon : state.kind === "queued" ? ClockIcon : RefreshCwIcon;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "mt-6 flex items-start gap-3 rounded-xl border px-4 py-3",
        state.kind === "failed"
          ? "border-amber-500/30 bg-amber-400/10"
          : "border-sky-500/30 bg-sky-500/[0.06]",
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          "mt-0.5 size-4 shrink-0",
          state.kind === "failed"
            ? "text-amber-700 dark:text-amber-300"
            : "text-sky-700 dark:text-sky-300",
          state.kind === "running" && "animate-spin motion-reduce:animate-none",
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{heading}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Link
          to="/$org/compliance/inspector/$threadId"
          params={{ org: orgSegment, threadId: state.run.threadId }}
          className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-foreground hover:underline"
        >
          {state.kind === "failed" ? "Open the run" : "Watch the run"}
          <ArrowUpRightIcon aria-hidden className="size-3" />
        </Link>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {state.kind === "failed"
            ? `stopped ${timeAgo(state.run.completedAt ?? state.run.startedAt, now)}`
            : `started ${timeAgo(state.run.startedAt, now)}`}
        </span>
      </div>
    </div>
  );
}
